import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    readConsent,
    acceptAll,
    rejectAll,
    writeConsent,
    initConsentDefaults,
    COOKIE_EVENTS,
} from "./cookieConsent";

const CATEGORIES = [
    {
        key: "necessary",
        title: "Cookies necesarias",
        desc: "Imprescindibles para el funcionamiento del sitio web. No pueden desactivarse.",
        locked: true,
    },
    {
        key: "analytics",
        title: "Cookies analíticas",
        desc: "Nos ayudan a entender cómo se utiliza el sitio para poder mejorarlo. Anónimas y agregadas.",
    },
    {
        key: "personalization",
        title: "Cookies de personalización",
        desc: "Recuerdan preferencias del usuario (por ejemplo, idioma) para mejorar la experiencia.",
    },
    {
        key: "marketing",
        title: "Cookies de marketing",
        desc: "Permiten mostrar contenidos y campañas más relevantes. Este sitio no las utiliza actualmente.",
    },
];

export default function CookieBanner() {
    const [state, setState] = useState(null); // null = aún no evaluado
    const [showPrefs, setShowPrefs] = useState(false);
    const [prefs, setPrefs] = useState({
        necessary: true,
        analytics: false,
        personalization: false,
        marketing: false,
    });

    useEffect(() => {
        initConsentDefaults();
        const existing = readConsent();
        setState(existing);
        if (existing) {
            setPrefs({
                necessary: true,
                analytics: !!existing.analytics,
                personalization: !!existing.personalization,
                marketing: !!existing.marketing,
            });
        }

        const onOpen = () => setShowPrefs(true);
        window.addEventListener(COOKIE_EVENTS.OPEN, onOpen);
        return () => window.removeEventListener(COOKIE_EVENTS.OPEN, onOpen);
    }, []);

    const needsDecision = state === null || !state?.decidedAt;
    const bannerVisible = needsDecision || showPrefs;

    const doAcceptAll = () => {
        const next = acceptAll();
        setState(next);
        setShowPrefs(false);
    };

    const doRejectAll = () => {
        const next = rejectAll();
        setState(next);
        setShowPrefs(false);
    };

    const doSavePrefs = () => {
        const next = writeConsent(prefs);
        setState(next);
        setShowPrefs(false);
    };

    if (!bannerVisible) return null;

    return (
        <>
            {/* Backdrop suave solo cuando el panel de preferencias está abierto */}
            {showPrefs && (
                <div
                    className="fixed inset-0 bg-black/30 z-[70] backdrop-blur-sm"
                    onClick={() => setShowPrefs(false)}
                    aria-hidden="true"
                />
            )}

            {!showPrefs && needsDecision && (
                <div
                    role="dialog"
                    aria-live="polite"
                    aria-label="Aviso de cookies"
                    data-testid="cookie-banner"
                    className="fixed left-3 right-3 sm:left-6 sm:right-auto sm:max-w-md bottom-[calc(0.75rem+env(safe-area-inset-bottom))] sm:bottom-6 z-[70]"
                >
                    <div className="bg-white border border-[#082050]/15 shadow-xl p-4 sm:p-6">
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-2 sm:mb-3">
                            Cookies
                        </div>
                        <h2 className="text-base sm:text-lg font-semibold text-[#082050] tracking-tight">
                            Respetamos tu privacidad
                        </h2>
                        <p className="mt-2 sm:mt-3 text-[13px] sm:text-sm text-[#4A5568] leading-relaxed">
                            Utilizamos cookies propias y de terceros para asegurar el
                            correcto funcionamiento del sitio, analizar el uso de forma
                            anónima y mejorar la experiencia. Puedes aceptar todas,
                            rechazarlas o configurar tus preferencias. Consulta nuestra{" "}
                            <Link
                                to="/politica-de-cookies"
                                className="underline text-[#082050] hover:no-underline"
                            >
                                Política de Cookies
                            </Link>
                            .
                        </p>

                        <div className="mt-4 sm:mt-6 grid grid-cols-3 gap-2">
                            <button
                                type="button"
                                onClick={doRejectAll}
                                data-testid="btn-cookie-reject"
                                className="border border-[#082050]/30 text-[#082050] px-2 sm:px-4 py-2.5 text-[13px] sm:text-sm font-medium hover:bg-[#082050]/5"
                            >
                                Rechazar
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowPrefs(true)}
                                data-testid="btn-cookie-configure"
                                className="border border-[#082050]/30 text-[#082050] px-2 sm:px-4 py-2.5 text-[13px] sm:text-sm font-medium hover:bg-[#082050]/5"
                            >
                                Configurar
                            </button>
                            <button
                                type="button"
                                onClick={doAcceptAll}
                                data-testid="btn-cookie-accept"
                                className="bg-[#082050] text-white px-2 sm:px-4 py-2.5 text-[13px] sm:text-sm font-medium hover:bg-[#082050]/90"
                            >
                                Aceptar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showPrefs && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Configuración de cookies"
                    data-testid="cookie-preferences"
                    className="fixed inset-0 z-[71] flex items-end sm:items-center justify-center p-3 sm:p-6"
                >
                    <div className="bg-white w-full sm:max-w-lg border border-[#082050]/15 shadow-xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 sm:p-8">
                            <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-3">
                                Preferencias
                            </div>
                            <h2 className="text-xl sm:text-2xl font-semibold text-[#082050] tracking-tight">
                                Configura tus cookies
                            </h2>
                            <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">
                                Activa o desactiva cada categoría según tus preferencias.
                                Puedes cambiar esta configuración cuando quieras desde el
                                pie de página.
                            </p>

                            <div className="mt-6 divide-y divide-[#E2E4E9] border-y border-[#E2E4E9]">
                                {CATEGORIES.map((cat) => (
                                    <label
                                        key={cat.key}
                                        className="flex items-start gap-4 py-4 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="text-sm font-semibold text-[#082050]">
                                                {cat.title}
                                            </div>
                                            <div className="mt-1 text-xs text-[#4A5568] leading-relaxed">
                                                {cat.desc}
                                            </div>
                                        </div>
                                        <input
                                            type="checkbox"
                                            data-testid={`cookie-toggle-${cat.key}`}
                                            checked={!!prefs[cat.key]}
                                            disabled={cat.locked}
                                            onChange={(e) =>
                                                setPrefs((p) => ({
                                                    ...p,
                                                    [cat.key]: e.target.checked,
                                                }))
                                            }
                                            className="mt-1 accent-[#082050] h-4 w-4"
                                        />
                                    </label>
                                ))}
                            </div>

                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <button
                                    type="button"
                                    onClick={doRejectAll}
                                    data-testid="btn-prefs-reject"
                                    className="border border-[#082050]/30 text-[#082050] px-4 py-2.5 text-sm font-medium hover:bg-[#082050]/5"
                                >
                                    Rechazar todo
                                </button>
                                <button
                                    type="button"
                                    onClick={doSavePrefs}
                                    data-testid="btn-prefs-save"
                                    className="border border-[#082050] text-[#082050] px-4 py-2.5 text-sm font-medium hover:bg-[#082050]/5"
                                >
                                    Guardar preferencias
                                </button>
                                <button
                                    type="button"
                                    onClick={doAcceptAll}
                                    data-testid="btn-prefs-accept"
                                    className="bg-[#082050] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#082050]/90"
                                >
                                    Aceptar todo
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
